import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  EDIT_PAYMENT_FLOW_TYPE_COLUMNS,
  IN_HOUSEPART_PAYMENT,
  JODO_FLEX,
  KH_VERTICAL_ID,
  PART_PAYMENT_PROVIDER_STATUS,
} from "constants/config";
import {
  useCancelMandate,
  useFetchOrderPaymentFlows,
} from "store/backend/assets";
import { Dialog, ErrorButton, PrimaryButton, Table } from "widgets";
import FullPageLoader from "Shared/FullPageLoader/FullPageLoader";
import ErrorFallback from "components/ErrorFallBack/ErrorFallback";
import SwitchProvider from "components/SwitchProvider/SwitchProvider";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./EditPaymentFlowType.module.scss";

const handleOnClickCancelMandateButton = (
  orderId,
  cancelMandate,
  openSnackbar
) => {
  if (!orderId) {
    return;
  }
  cancelMandate(orderId, {
    onSuccess: () => {
      window.location.reload();
      openSnackbar("Successfuly cancelled mandate", "success");
    },
    onError: (error) => {
      openSnackbar(`${error.message}`, "error");
    },
  });
};

const getActionButton = (
  flow,
  verticalId,
  setOpenDialog,
  orderId,
  cancelMandate,
  isCancelMandateLoading,
  openSnackbar
) => {
  const isKHVertical = Number(verticalId) === Number(KH_VERTICAL_ID);
  const isActive = flow.status?.id === PART_PAYMENT_PROVIDER_STATUS.ACTIVE;
  const isInHousePartPaymentProvider = flow.name === IN_HOUSEPART_PAYMENT.name;

  const showCancelMandateButton = [
    PART_PAYMENT_PROVIDER_STATUS.CREATED,
    PART_PAYMENT_PROVIDER_STATUS.APPROVED,
    PART_PAYMENT_PROVIDER_STATUS.SETUP,
  ].includes(flow.status?.id);

  if (isKHVertical && isActive && isInHousePartPaymentProvider) {
    return (
      <div className={styles.actions}>
        <PrimaryButton onClick={() => setOpenDialog(true)}>
          Switch to a different provider
        </PrimaryButton>
      </div>
    );
  }

  if (showCancelMandateButton && !isInHousePartPaymentProvider) {
    return (
      <ErrorButton
        onClick={() =>
          handleOnClickCancelMandateButton(orderId, cancelMandate, openSnackbar)
        }
      >
        {isCancelMandateLoading ? "Cancelling..." : "Cancel"}
      </ErrorButton>
    );
  }

  return "N/A";
};

const getStatusName = (flow) => {
  if (flow.id !== JODO_FLEX.id) return flow.status.name;
  const status = flow.status.id;
  if (status === PART_PAYMENT_PROVIDER_STATUS.CREATED)
    return "e-mandate pending";

  if (status === PART_PAYMENT_PROVIDER_STATUS.SETUP)
    return "e-mandate set";

  if (status === PART_PAYMENT_PROVIDER_STATUS.APPROVED)
    return "e-mandate approved";

  if (status === PART_PAYMENT_PROVIDER_STATUS.CANCELLED)
    return "e-mandate cancelled";

  if (status === PART_PAYMENT_PROVIDER_STATUS.EXPIRED)
    return "e-mandate expired";

  return flow.status.name;
};

export const getRows = (
  paymentFlowsDetails,
  verticalId,
  setOpenDialog,
  orderId,
  cancelMandate,
  isCancelMandateLoading,
  openSnackbar
) => {
  if (!paymentFlowsDetails || paymentFlowsDetails.length === 0) return [];

  return paymentFlowsDetails.map((flow) => ({
    provider: flow.name,
    paymentFlowStatus: getStatusName(flow),
    numberOfParts: flow.noOfParts,
    timeStamp: new Date(flow.updatedAt).toLocaleString(),
    actions: getActionButton(
      flow,
      verticalId,
      setOpenDialog,
      orderId,
      cancelMandate,
      isCancelMandateLoading,
      openSnackbar
    ),
  }));
};

const EditPaymentFlowType = () => {
  const { openSnackbar } = useSnackbarContext();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const verticalId = searchParams.get("verticalId");
  const [openDialog, setOpenDialog] = useState(false);
  const {
    data: paymentFlowsDetails,
    isPending: isFetchPaymentFlowsPending,
    isError: isErrorOnPaymentFlows,
    error: errorMessageOnPaymentFlows,
  } = useFetchOrderPaymentFlows(orderId);

  const { mutate: cancelMandate, isPending: isCancelMandateLoading } =
    useCancelMandate();

  if (isFetchPaymentFlowsPending) return <FullPageLoader />;

  if (isErrorOnPaymentFlows)
    return <ErrorFallback message={errorMessageOnPaymentFlows.message} />;

  return (
    <div>
      <div className={styles.header}>Edit Payment Flow Type</div>
      {paymentFlowsDetails.length === 0 ? (
        <div className={styles.noPaymentFlowsMessage}>
          No payment flows available for this order.
        </div>
      ) : (
        <div className={styles.paymentFlowsTable}>
          <Table
            columns={EDIT_PAYMENT_FLOW_TYPE_COLUMNS}
            rows={getRows(
              paymentFlowsDetails,
              verticalId,
              setOpenDialog,
              orderId,
              cancelMandate,
              isCancelMandateLoading,
              openSnackbar
            )}
          />
        </div>
      )}
      <Dialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        height="auto"
      >
        <SwitchProvider
          onClose={() => setOpenDialog(false)}
          orderId={orderId}
        />
      </Dialog>
    </div>
  );
};
export default EditPaymentFlowType;
